import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  Col,
  Spinner,
  Input,
  InputGroup,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AwesomePagination from "../../../Components/Common/AwesomePagination";
import { toast } from "react-toastify";
import { APIClient } from "../../../helpers/api_helper";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import FeatherIcon from "feather-icons-react";

const apiClient = new APIClient();

const Transacoes = () => {
  const [loading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [termo, setTermo] = useState("");
  const [inputTermo, setInputTermo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [transacoes, setTransacoes] = useState([]);
  const [coins, setCoins] = useState([]);

  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchPrices = useCallback(async () => {
    const response = await apiClient.get(`/moedas/precos`);
    if (response.sucesso) {
      setCoins(response.dados);
    } else {
      toast.error(response.mensagem);
    }
  }, []);

  const getTransacoes = useCallback(async () => {
    setLoading(true);
    let data = {
      user_id: user.data._id, // ID do usuário logado
      pagina: pagina,
      itens_pagina: 20,
      order_by: "created_at",
      order: "desc",
    };

    if (termo && termo !== "") {
      data.termo = termo;
    }

    const response = await apiClient.post("/cripto-td/transacoes", data);

    if (response.sucesso) {
      setTransacoes(response.dados.data);
      setTotalPaginas(response.dados.last_page);
      setTotalItens(response.dados.total);
    } else {
      toast.error(response.mensagem);
    }
    setLoading(false);
  }, [pagina, termo, user.data._id]);

  const copyToClipboard = async (text) => {
    if (!navigator.clipboard) {
      toast.error("API de Clipboard não suportada no seu navegador.");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para a área de transferência!");
    } catch (err) {
      toast.error("Falha ao copiar o texto");
    }
  };

  const formatCurrency = (value) => {
    if (!value) {
      return "R$ 0,00";
    }
    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    } catch (error) {
      return "R$ 0,00";
    }
  };

  function splitDateTime(dateTimeStr) {
    const dateTime = parseISO(dateTimeStr);
    const data = format(dateTime, "dd MMM, yyyy", { locale: ptBR });
    const hora = format(dateTime, "hh:mm a");
    return { data, hora };
  }

  const getTypeDisplay = (tipo) => {
    if (tipo === "compra_diaria") {
      return <span className="type text-success">Compra Diária</span>;
    }
    return <span className="type text-danger">{tipo}</span>;
  };

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        await getTransacoes();
        setShouldFetch(false);
      };
      fetchData();
    }
  }, [shouldFetch, getTransacoes]);

  document.title = "Transações | Painel Fort Exchange";

  const handleTermoKeyDown = (e) => {
    if (e.key === "Enter") {
      setTermo(inputTermo);
      setPagina(1);
      setShouldFetch(true);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Transações" pageTitle="Transações" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center border-0">
                  <h5 className="card-title mb-0 flex-grow-1">
                    Suas Transações ({totalItens})
                  </h5>
                </CardHeader>
                <CardBody>
                  {/* Filtros */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <InputGroup>
                        <div className="input-group-text">
                          <FeatherIcon icon="search" className="icon-dual" />
                        </div>
                        <Input
                          type="text"
                          placeholder="Buscar por moeda ou tipo de operação..."
                          value={inputTermo}
                          onChange={(e) => setInputTermo(e.target.value)}
                          onKeyDown={handleTermoKeyDown}
                        />
                      </InputGroup>
                    </Col>
                  </Row>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        padding: "20px",
                      }}
                    >
                      <Spinner height={100} width={100} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Data</th>
                              <th>Moeda</th>
                              <th>Tipo</th>
                              <th>Comprado</th>
                              <th>Quantidade</th>
                              <th>Cotação no Dia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transacoes.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="text-center text-muted py-4"
                                >
                                  Nenhuma transação encontrada.
                                </td>
                              </tr>
                            ) : (
                              transacoes.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  {/* Data */}
                                  <td>
                                    {splitDateTime(item.created_at).data}{" "}
                                    <small className="text-muted">10:00</small>
                                  </td>

                                  {/* Moeda */}
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="flex-shrink-0">
                                        <img
                                          src={`https://api.fort.exchange/crypto-icons/${
                                            item.moeda === "POL"
                                              ? "matic"
                                              : item.moeda.toLowerCase()
                                          }.svg`}
                                          alt=""
                                          className="avatar-xxs"
                                        />
                                      </div>
                                      <Link
                                        to="#"
                                        className="currency_name flex-grow-1 ms-2 text-body"
                                      >
                                        {item.moeda}
                                      </Link>
                                    </div>
                                    <span className="text-muted">
                                      <b>Conta: </b>
                                      {item.conta?.nome || "-"}
                                    </span>
                                  </td>

                                  {/* Tipo */}
                                  <td>{getTypeDisplay(item.tipo_operacao)}</td>

                                  {/* Comprado */}
                                  <td>
                                    {formatCurrency(item.valor_brl_original)}
                                  </td>

                                  {/* Quantidade */}
                                  <td>
                                    {(() => {
                                      const moeda = coins.find(
                                        (coin) => coin.moeda === item.moeda
                                      );
                                      const comprado_moeda = parseFloat(
                                        item.token_enviado || 0
                                      ).toFixed(moeda?.decimais || 8);

                                      return (
                                        <h6
                                          className="text-info amount mb-1"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            copyToClipboard(comprado_moeda)
                                          }
                                          title="Clique para copiar o valor"
                                        >
                                          {comprado_moeda}{" "}
                                          <FeatherIcon
                                            icon="copy"
                                            style={{
                                              color: "#ccc",
                                              height: "15px",
                                            }}
                                          />
                                        </h6>
                                      );
                                    })()}
                                  </td>

                                  {/* Cotação no Dia */}
                                  <td>{formatCurrency(item.cotacao_fort)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      <AwesomePagination
                        pagina={pagina}
                        totalPaginas={totalPaginas}
                        setPagina={(p) => {
                          setPagina(p);
                          setShouldFetch(true);
                        }}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Transacoes;
